import React, { useState, useEffect } from 'react';
import RevenueCard from '../components/RevenueCard';
import OccupancyRateCard from '../components/OccupancyRateCard';
import { Dropdown } from '../components/Dropdown';
import ProgressBar from '../components/ProgressBar';
import ReservationsIcon from '../Assets/ReservationsIcon.svg';
import NightIcon from '../Assets/PerNightIcon.svg';
// import AverageRate from '../components/AverageRate';
import NetRevenus from '../components/NetRevenus';
import LatestNoift from '../components/LatestNoift';
import '@google/model-viewer';
import {
    Container,
    CardContainer,
    DropdownContainer,
    RowContainer,
    ModelLoadingWrapper,
    ModelRunner,
    CalH2,
    ErrorP,
    ErrorWarp,
    ErrorImg,
    NoteP,
    LoadingText,
    StatValue,
    StatUnit
} from '../style-comps/DashboardHome.styles';
import { getListingsByUserId, getBatchReportingData } from '../services/api';
import runningModel from '../Assets/models/running.glb';

const DashboardHome = () => {
    const [properties, setProperties] = useState([]);
    const [dates, setDates] = useState([]);
    const [selectedListingId, setSelectedListingId] = useState(null);
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [allReports, setAllReports] = useState({});
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [progress, setProgress] = useState(0);
    const [fadeOut, setFadeOut] = useState(false);
    const [showLoading, setShowLoading] = useState(true);
    const [activities, setActivities] = useState([]);
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [hasNoListings, setHasNoListings] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [isLoadingMonth, setIsLoadingMonth] = useState(false);


    // Date dropdown Options
    const generateDateOptions = () => {
        const options = [];
        const now = new Date();
        const currentMonthStr = now.toISOString().slice(0, 7); // "2025-06"

        const minAllowed = new Date("2025-06-01"); // 只顯示這之後的月份

        for (let i = -3; i <= 3; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() + i, 1);

            if (date < minAllowed) continue; // ⛔️ 過濾早於 2025-07

            const year = date.getFullYear();
            const month = date.getMonth();

            const startDate = new Date(Date.UTC(year, month, 1)).toISOString().split('T')[0];
            const endDate = new Date(Date.UTC(year, month + 1, 0)).toISOString().split('T')[0];
            const label = new Date(Date.UTC(year, month, 1)).toLocaleString('en-US', {
                month: 'long',
                year: 'numeric',
                timeZone: 'UTC'
            });

            const value = `${startDate}_${endDate}`;
            const isCurrent = startDate.slice(0, 7) === currentMonthStr;

            options.push({ label, startDate, endDate, value, isCurrent });
        }

        return options;
    };


    // init render data 
    useEffect(() => {
        const init = async () => {
            const token = localStorage.getItem("authToken");
            const userId = localStorage.getItem("userId");

            if (!token || !userId || userId === "null" || userId === "") {
                console.warn("❌ DashboardHome: 無效的 userId 或 token，導回登入頁");
                setApiError("Invalid session. Please login again.");
                setShowLoading(false);
                return;
            }

            try {
                const listings = await getListingsByUserId(userId, token);
                const parentOnly = listings.filter((l) => l.ParentListingId === null);

                if (!listings || listings.length === 0 || parentOnly.length === 0) {
                    setHasNoListings(true);
                    setShowLoading(false);
                    return;
                }

                // Service On 的才能選擇 & 加入加總
                const serviceOnListings = parentOnly.filter((l) => l.IsServicePms === true);

                // Service Off 的只顯示標籤，不能選
                const serviceOffListings = parentOnly.filter((l) => l.IsServicePms === false);

                let propertyOptions = [
                    // Service On：正常可選
                    ...serviceOnListings.map((l) => ({
                        label: l.KozyGuruName || l.Name || `Listing ${l.Id}`,
                        value: l.Id || l.id,
                    })),
                    // Service Off：顯示但 disabled
                    ...serviceOffListings.map((l) => ({
                        label: `${l.KozyGuruName || l.Name || `Listing ${l.Id}`} (Service Off)`,
                        value: l.Id || l.id,
                        disabled: true,   // 👈 傳給 Dropdown 用來擋點擊
                    })),
                ];

                // ALL 只加總 Service On 的房源
                if (serviceOnListings.length > 1) {
                    propertyOptions.unshift({
                        label: 'All Properties',
                        value: 'ALL'
                    });
                }

                const dateOptions = generateDateOptions();

                setProperties(propertyOptions);
                setDates(dateOptions);

                const currentMonthStr = new Date().toISOString().slice(0, 7);
                const currentMonthOption = dateOptions.find(d => d.startDate.slice(0, 7) === currentMonthStr);
                if (currentMonthOption) {
                    setSelectedDateRange(currentMonthOption);
                }

                setIsInitialized(true);
            } catch (error) {
                const status = error?.response?.status;
                const message = error?.response?.data?.message || error.message;

                console.error("init() 發生錯誤：", { status, message });

                if (status === 404) {
                    setHasNoListings(true);
                } else if (status === 500) {
                    setApiError("Please refresh or try again later.");
                } else {
                    setApiError("Error loading data.");
                }

                setShowLoading(false);
            }
        };

        init();
    }, []);



    //設定 selectedListingId 預設為 'ALL'（如果有多個房源）
    useEffect(() => {
        if (isInitialized && properties.length > 0) {
            setSelectedListingId(
                properties.length > 1 ? 'ALL' : properties[0].value
            );
        }
    }, [isInitialized, properties]);


    useEffect(() => {
        const fetchAllReports = async () => {
            if (!selectedListingId || dates.length === 0) return;

            const cacheKey = `dashboard_reports_${selectedListingId}`;
            const storedReports = localStorage.getItem(cacheKey);
            let parsedCache = null;
            let isCacheValid = false;

            // ✅ 快取存在時檢查時間戳
            if (storedReports) {
                try {
                    parsedCache = JSON.parse(storedReports);
                    const now = Date.now();
                    const age = now - (parsedCache.timestamp || 0);
                    const ONE_DAY = 24 * 60 * 60 * 1000;

                    if (age < ONE_DAY) {
                        isCacheValid = true;
                    } else {
                        console.log(`快取已超過 24 小時 (${Math.round(age / 3600000)} 小時)，重新抓取報表`);
                    }
                } catch {
                    console.warn("無法解析快取 JSON，將重新抓取報表");
                }
            }

            // 若快取仍有效 → 直接載入
            if (isCacheValid && parsedCache?.data) {
                console.log(`📦 使用快取報表: ${cacheKey}`);
                setAllReports(parsedCache.data);

                const currentMonthStr = new Date().toISOString().slice(0, 7);
                const latest = dates.find(d => d.startDate.slice(0, 7) === currentMonthStr);
                const latestKey = latest?.value;

                if (!selectedDateRange) {
                    setSelectedDateRange(latest);
                    setReportData(parsedCache.data[latestKey]);
                } else {
                    const existingKey = selectedDateRange?.value;
                    setReportData(parsedCache.data[existingKey]);
                }

                setShowLoading(false);
                return; // 🚀 使用快取就不打 API
            }

            // 無快取或過期 → 重新打 API
            setIsLoadingReports(true);
            setProgress(0);
            const loadingMessages = [
                "Connecting...",
                "Loading reports...",
                "Almost there..."
            ];
            let msgIndex = 0;
            setProgress(msgIndex);
            const interval = setInterval(() => {
                msgIndex = Math.min(msgIndex + 1, loadingMessages.length - 1);
                setProgress(msgIndex);
            }, 1500);

            try {
                const now = new Date();
                const allStart = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1))
                    .toISOString().split('T')[0];
                const allEnd = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 2, 0))
                    .toISOString().split('T')[0];

                const individualIds = properties.filter(p => p.value !== 'ALL' && !p.disabled).map(p => p.value);
                const queryIds = selectedListingId === 'ALL' ? individualIds : [selectedListingId];

                console.log('🚀 API 開始打', { queryIds, allStart, allEnd });
                const t1 = Date.now();

                const batchResponse = await getBatchReportingData(queryIds, allStart, allEnd);

                console.log(`✅ API 回來了！花了 ${Date.now() - t1} ms`);
                console.log('📦 batchResponse:', batchResponse);

                const successList = batchResponse.Success || [];

                // MonthKey("2026-01") → dateKey("2026-01-01_2026-01-31") 對照表
                const monthKeyToDateKey = {};
                dates.forEach(({ startDate, value }) => {
                    const mk = startDate.slice(0, 7);
                    monthKeyToDateKey[mk] = value;
                });

                // 初始化 reportMap
                const reportMap = {};
                dates.forEach(({ value }) => {
                    reportMap[value] = {};
                });

                if (selectedListingId === 'ALL') {
                    // 合併所有房源同一個月的資料
                    successList.forEach((listing) => {
                        (listing.Months || []).forEach(({ MonthKey, Reporting }) => {
                            const dateKey = monthKeyToDateKey[MonthKey];
                            if (!dateKey) return;

                            if (!reportMap[dateKey].TotalOwnerRevenue) {
                                reportMap[dateKey] = {
                                    TotalOwnerRevenue: 0,
                                    TotalReservations: 0,
                                    TotalNights: 0,
                                    _AverageNightsList: [],
                                    _OccupancyList: [],
                                    _AverageRevenueList: [],
                                    Currency: Reporting.Currency
                                };
                            }

                            const acc = reportMap[dateKey];
                            acc.TotalOwnerRevenue += (Reporting.TotalOwnerRevenue || 0);
                            acc.TotalReservations += (Reporting.TotalReservations || 0);
                            acc.TotalNights += (Reporting.TotalNights || 0);
                            acc._AverageNightsList.push(Reporting.AverageNights || 0);
                            acc._OccupancyList.push(Reporting.OccupancyRate || 0);
                            acc._AverageRevenueList.push(Reporting.AverageRevenuePerNight || 0);
                        });

                        // 同步快取每一間單獨房源
                        const listingId = listing.ListingId;
                        const singleCacheKey = `dashboard_reports_${listingId}`;
                        const existingRaw = localStorage.getItem(singleCacheKey);
                        let existingData = {};
                        if (existingRaw) {
                            try { existingData = JSON.parse(existingRaw).data || {}; } catch { existingData = {}; }
                        }
                        (listing.Months || []).forEach(({ MonthKey, Reporting }) => {
                            const dateKey = monthKeyToDateKey[MonthKey];
                            if (dateKey) existingData[dateKey] = Reporting;
                        });
                        localStorage.setItem(singleCacheKey, JSON.stringify({ timestamp: Date.now(), data: existingData }));
                        console.log(`已更新房源 ${listingId} 所有月份報表`);
                    });

                } else {
                    // 單一房源：直接把每個月份的 Reporting 存入 reportMap
                    const listingData = successList[0];
                    if (listingData) {
                        (listingData.Months || []).forEach(({ MonthKey, Reporting }) => {
                            const dateKey = monthKeyToDateKey[MonthKey];
                            if (dateKey) reportMap[dateKey] = Reporting;
                        });
                    }
                }

                // 存入快取並附時間戳
                const cachePayload = {
                    timestamp: Date.now(),
                    data: reportMap
                };
                localStorage.setItem(cacheKey, JSON.stringify(cachePayload));
                console.log(`已快取報表: ${cacheKey}`);

                setAllReports(reportMap);
                const currentMonthStr = new Date().toISOString().slice(0, 7);
                const latest = dates.find(d => d.startDate.slice(0, 7) === currentMonthStr);
                const latestKey = latest?.value;
                if (!hasFetchedOnce) {
                    setSelectedDateRange(latest);
                    setReportData(reportMap[latestKey]);
                    setHasFetchedOnce(true);
                } else if (selectedDateRange) {
                    setReportData(reportMap[selectedDateRange.value]);
                }

            } catch (err) {
                console.error("批次報表錯誤", err);
                setApiError("Something went wrong. Please refresh or try again later.");
            } finally {
                clearInterval(interval);
                setProgress(100);
                setFadeOut(true);
                setTimeout(() => {
                    setIsLoadingReports(false);
                    setShowLoading(false);
                }, 600);
            }
        };

        if (selectedListingId && dates.length > 0) {
            fetchAllReports();
        }
    }, [selectedListingId, dates]);


    // dropdown handleDateChange
    const handleDateChange = async (val) => {
        const selected = dates.find(d => d.value === val);
        setSelectedDateRange(selected);

        // 已有資料 → 直接顯示
        if (selected && allReports[selected.value] && Object.keys(allReports[selected.value]).length > 0) {
            setReportData(allReports[selected.value]);
            return;
        }

        // 沒有資料 → 補抓這個月
        setIsLoadingMonth(true);
        try {
            const individualIds = properties.filter(p => p.value !== 'ALL').map(p => p.value);
            const idsToFetch = selectedListingId === 'ALL' ? individualIds : [selectedListingId];
            const v2Response = await getBatchReportingData(idsToFetch, selected.startDate, selected.endDate);
            const successList = v2Response?.Success || [];

            const newReports = { ...allReports };

            if (selectedListingId === 'ALL') {
                newReports[val] = {};
                successList.forEach((listingData) => {
                    const months = listingData.Months || [];
                    months.forEach(m => {
                        if (m.MonthKey !== selected.startDate.slice(0, 7)) return;
                        const r = m.Reporting;
                        const acc = newReports[val];
                        acc.TotalOwnerRevenue = (acc.TotalOwnerRevenue || 0) + (r.TotalOwnerRevenue || 0);
                        acc.TotalReservations = (acc.TotalReservations || 0) + (r.TotalReservations || 0);
                        acc.TotalNights = (acc.TotalNights || 0) + (r.TotalNights || 0);
                        (acc._AverageNightsList = acc._AverageNightsList || []).push(r.AverageNights || 0);
                        (acc._OccupancyList = acc._OccupancyList || []).push(r.OccupancyRate || 0);
                        (acc._AverageRevenueList = acc._AverageRevenueList || []).push(r.AverageRevenuePerNight || 0);
                        acc.Currency = r.Currency;
                    });
                });
            } else {
                const listingData = successList.find(s => s.ListingId === Number(selectedListingId)) || successList[0];
                const months = listingData?.Months || [];
                const match = months.find(m => m.MonthKey === selected.startDate.slice(0, 7));
                newReports[val] = match?.Reporting || {};
            }

            setAllReports(newReports);
            setReportData(newReports[val]);

            const cacheKey = `dashboard_reports_${selectedListingId}`;
            localStorage.setItem(cacheKey, JSON.stringify({
                timestamp: Date.now(),
                data: newReports
            }));
        } catch (err) {
            console.error("❌ 補抓月份報表失敗", err);
        } finally {
            setIsLoadingMonth(false);
        }
    };


    useEffect(() => {
        const fetchActivities = async () => {
            const act = await new Promise((resolve) =>
                setTimeout(() =>
                    resolve([
                        { description: 'Coming Soon', date: '' },
                    ]),
                    1000
                )
            );
            setActivities(act);
        };
        fetchActivities();
    }, []);

    if (showLoading) {
        return (
            <ModelLoadingWrapper fadeOut={fadeOut}>
                <LoadingText>
                    {apiError ? apiError : ["Connecting...", "Loading reports...", "Almost there..."][progress] || "Almost there..."}
                </LoadingText>
                <ModelRunner>
                    <model-viewer
                        src={runningModel}
                        autoplay
                        animation-name="Run"
                        camera-controls
                    />
                </ModelRunner>
            </ModelLoadingWrapper>
        );
    }


    //本月 / 上月報表資料
    const thisKey = selectedDateRange?.value;
    const thisData = allReports[thisKey] || {};
    const thisIndex = dates.findIndex(d => d.value === thisKey);
    const prevKey = dates[thisIndex - 1]?.value;
    const prevData = allReports[prevKey] || {};
    const revenueCurr = thisData?.TotalOwnerRevenue || 0;
    const revenuePrev = prevData?.TotalOwnerRevenue || 0;
    const revenueDiff = revenueCurr - revenuePrev;

    // 成長率：Reservations
    const thisVal = thisData.TotalReservations || 0;
    const prevVal = prevData.TotalReservations || 0;
    let growthText = '';
    if (prevVal > 0) {
        const growth = ((thisVal - prevVal));
        growthText = `${growth > 0 ? '+ ' : ''}${growth.toFixed(0)}`;
    }

    // 成長率：Average Reservation Length
    const avgCurr =
        selectedListingId === 'ALL' && Array.isArray(thisData._AverageNightsList)
            ? (() => {
                const valid = thisData._AverageNightsList.filter(val => typeof val === 'number' && !isNaN(val) && val > 0);
                if (valid.length === 0) return 0;
                return parseFloat((valid.reduce((a, b) => a + b, 0) / valid.length).toFixed(2));
            })()
            : (typeof thisData.AverageNights === 'number' ? parseFloat(thisData.AverageNights.toFixed(2)) : 0);

    // 定義前一月平均值（正確邏輯）
    const avgPrev =
        selectedListingId === 'ALL' && Array.isArray(prevData._AverageNightsList)
            ? (() => {
                const valid = prevData._AverageNightsList.filter(val => typeof val === 'number' && !isNaN(val) && val > 0);
                if (valid.length === 0) return 0;
                return parseFloat((valid.reduce((a, b) => a + b, 0) / valid.length).toFixed(2));
            })()
            : (typeof prevData.AverageNights === 'number' ? parseFloat(prevData.AverageNights.toFixed(2)) : 0);

    // 成長率：Average Reservation Length
    let avgGrowthText = '';
    if (avgPrev > 0) {
        const growth = ((avgCurr - avgPrev));
        avgGrowthText = `${growth > 0 ? '+ ' : ''}${growth.toFixed(1)}`;
    }

    const shortRange = [];

    if (thisIndex > 0) {
        shortRange.push(dates[thisIndex - 1]);
    }
    if (dates[thisIndex]) {
        shortRange.push(dates[thisIndex]);
    }
    if (thisIndex < dates.length - 1) {
        shortRange.push(dates[thisIndex + 1]);
    }


    const shortLabels = shortRange.map(d => d.label);
    const completed = [];
    const future = [];

    const getAvgNights = (total, count) =>
        count > 0 ? parseFloat((total / count).toFixed(2)) : 0;

    shortRange.forEach((d) => {
        const data = allReports[d.value] || {};
        const rawTotal = data.TotalNights || 0;

        let value = 0;
        if (selectedListingId === 'ALL') {
            const count = properties.filter(p => p.value !== 'ALL').length;
            value = getAvgNights(rawTotal, count);
        } else {
            value = rawTotal;
        }

        if (d.value === thisKey) {
            completed.push(0);
            future.push(value);
        } else {
            completed.push(value);
            future.push(0);
        }
    });

    const nightsHighlightIndex = shortRange.findIndex(d => d.value === thisKey);

    const nightsChartData = {
        labels: shortLabels,
        completed: completed,
        future: future,
        highlight: nightsHighlightIndex,
    };


    // 找出今天是哪一個 index
    const now = new Date();
    const currentMonthStr = now.toISOString().slice(0, 7);
    const currentIndex = dates.findIndex(d => d.startDate.slice(0, 7) === currentMonthStr);

    //如果有資料，抓從當月往前推6個月
    const sixMonthRange = currentIndex >= 0
        ? dates.slice(currentIndex - 5, currentIndex + 1)
        : [];

    const avgRevenueLabels = sixMonthRange.map(d => d.label);


    const avgRevenueValues = sixMonthRange.map((d) => {
        const data = allReports[d.value] || {};

        let avg = 0;

        if (selectedListingId === 'ALL') {
            const list = data._AverageRevenueList || [];
            const valid = list.filter(val => typeof val === 'number' && !isNaN(val) && val > 0);
            avg = valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
        } else {
            avg = typeof data.AverageRevenuePerNight === 'number' && !isNaN(data.AverageRevenuePerNight)
                ? data.AverageRevenuePerNight
                : 0;
        }

        return parseFloat(avg.toFixed(2));
    });


    const avgRevenueHighlightIndex = sixMonthRange.findIndex(d => d.value === thisKey);

    const avgRevenueChartData = {
        labels: avgRevenueLabels,
        completed: avgRevenueValues.map((val, idx) => (idx === avgRevenueHighlightIndex ? 0 : val)),
        future: avgRevenueValues.map((val, idx) => (idx === avgRevenueHighlightIndex ? val : 0)),
    };

    if (!showLoading && apiError) {
        return (
            <ErrorWarp>
                <ErrorImg src="../Assets/mrK05.png" />
                <ErrorP>{apiError || "Something went wrong. Please try again later."}</ErrorP>
            </ErrorWarp>
        );
    }


    if (hasNoListings) {
        return (
            <ErrorWarp>
                <ErrorImg src="../Assets/mrK05.png" />
                <ErrorP>Sorry, You Don't have any Property Listing</ErrorP>
            </ErrorWarp>
        );
    }

    return (
        <Container style={{ position: 'relative' }}>
            {isLoadingMonth && (
                <div style={{
                    position: 'fixed',        // ← absolute 改成 fixed
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(255,255,255,0.6)',
                    zIndex: 99999,            // ← zIndex 調高，確保蓋過所有元素
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <p style={{ fontSize: '18px', color: '#61CBC9', fontWeight: 'bold' }}>
                        Loading...
                    </p>
                </div>
            )}
            <RowContainer>
                <CalH2>
                    {selectedDateRange?.label || 'Loading...'}
                </CalH2>


                <DropdownContainer>
                    <Dropdown
                        options={properties}
                        defaultLabel="All Properties"
                        value={selectedListingId}
                        onChange={(val) => {
                            // 擋掉 Service Off 的房源
                            const selectedOption = properties.find(p => p.value === val);
                            if (selectedOption?.disabled) return;   // 👈 加這行

                            if (val !== selectedListingId) {
                                const cacheKey = `dashboard_reports_${val}`;
                                const storedReports = localStorage.getItem(cacheKey);
                                const hasCache = !!storedReports;

                                if (!hasCache) {
                                    if (properties.length >= 2) {
                                        setShowLoading(true);
                                        setFadeOut(false);
                                    }
                                }

                                setSelectedListingId(val);
                                setReportData(null);
                            }
                        }}

                        isOpen={openDropdown === 'property'}
                        onOpen={() => setOpenDropdown('property')}
                        onClose={() => setOpenDropdown(null)}
                    />

                    <Dropdown
                        options={dates}
                        defaultLabel="Select Month"
                        value={selectedDateRange?.value}
                        onChange={handleDateChange}
                        isOpen={openDropdown === 'date'}
                        onOpen={() => setOpenDropdown('date')}
                        onClose={() => setOpenDropdown(null)}
                    />



                </DropdownContainer>
            </RowContainer>



            <RowContainer>
                <CardContainer>
                    <RevenueCard
                        title="Revenue"
                        totalRevenue={revenueCurr}
                        lastEarned={revenueDiff}
                        currency={thisData?.Currency}
                    />
                    <OccupancyRateCard
                        occupancyRate={
                            selectedListingId === 'ALL' && Array.isArray(thisData._OccupancyList)
                                ? (() => {
                                    const valid = thisData._OccupancyList.filter(val => typeof val === 'number' && !isNaN(val) && val > 0);
                                    if (valid.length === 0) return '0.00';
                                    const avg = valid.reduce((a, b) => a + b, 0) / valid.length;
                                    return avg.toFixed(2);
                                })()
                                : (typeof thisData?.OccupancyRate === 'number' ? thisData.OccupancyRate.toFixed(2) : '0.00')
                        }
                    />



                    <ProgressBar
                        title="Reservations"
                        count={thisVal}
                        growth={growthText}
                        // percentage={(thisVal / 31) * 100}
                        icon={ReservationsIcon}
                    />
                    <ProgressBar
                        title={'Avg.Reservation Length'}
                        count={
                            <>
                                <StatValue>{avgCurr.toFixed(2)}</StatValue>
                                <StatUnit>/night</StatUnit>
                            </>
                        }
                        growth={avgGrowthText}
                        percentage={(avgCurr / 31) * 100}
                        icon={NightIcon}
                    />


                </CardContainer>
            </RowContainer>

            <RowContainer>
                <CardContainer>
                    <NetRevenus
                        chartData={nightsChartData}
                        xAxisLabel="Month"
                        yAxisLabel="Total Nights (Occupied) "
                    />
                    <LatestNoift activities={activities} />
                    {/* <NetRevenus
                        chartData={{
                            labels: avgRevenueLabels,
                            completed: avgRevenueValues,
                            future: avgRevenueValues,
                            highlight: avgRevenueHighlightIndex,
                        }}
                        xAxisLabel="Month"
                        yAxisLabel="Avg Revenue Per Night"
                    /> */}
                </CardContainer>


                {/* <AverageRate
                    title="Total Nights"
                    data={nightsRange}
                    highlight={highlightIndex}
                /> */}
                {/* <LatestNoift activities={activities} /> */}
            </RowContainer>

            <RowContainer>
                <NoteP>** Revenue figures are for reference only. <br /><br />
                    ** Estimates may occasionally vary, so please refer to the monthly statement for the actual earnings.<br /><br />
                    ** Net revenue after platform and management fees.
                </NoteP>

            </RowContainer>

        </Container >
    );
};

export default DashboardHome;